import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOff, Star, ShoppingCart } from 'lucide-react';
import { Product, ProductType, CollectionType, ProductCategory } from '../types';
import { ImageWithFallback } from './ImageWithFallback';

interface ProductDisplayBoxProps {
  product: Product;
  viewMode?: 'grid' | 'list';
  onAddToCart?: () => void;
  variant?: 'default' | 'featured';
}

export function ProductDisplayBox({ product, viewMode = 'grid', onAddToCart }: ProductDisplayBoxProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const isNewProduct = () => {
    const productDate = new Date(product.date_added || product.dateAdded);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - productDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const getCategoryColor = (category: ProductCategory) => {
    switch (category) {
      case 'New':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Like New':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Used':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className={`product-card-mobile block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
        viewMode === 'list' ? 'flex' : ''
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setCurrentImageIndex(0);
      }}
    >
      <div className={`relative ${viewMode === 'list' ? 'w-32 sm:w-64 flex-shrink-0' : ''}`}>
        <div className="aspect-square bg-gray-100 relative">
          <ImageWithFallback
            src={product.images[currentImageIndex]?.url}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain"
            fallbackText={product.name}
          />

          {/* Image Navigation Dots */}
          {product.images && product.images.length > 1 && isHovering && (
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
              {product.images.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    index === currentImageIndex ? 'bg-blue-600' : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Status Badges */}
          <div className="absolute top-2 right-2 product-badges">
            {product.stock_level > 0 ? (
              <span className="badge-instock">
                In Stock ({product.stock_level})
              </span>
            ) : (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
                Out of Stock
              </span>
            )}

            {product.category === 'New' && (
              <span className="badge-new">New</span>
            )}
            {product.category === 'Used' && (
              <span className="badge-used">Used</span>
            )}
          </div>
        </div>
      </div>

      <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
        <div className="mb-2">
          <span className="text-sm text-gray-500">{product.brand}</span>
          <h3 className="text-lg font-medium text-gray-900 line-clamp-2">
            {product.name}
          </h3>
        </div>

        <div className="flex items-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className="w-4 h-4 text-yellow-400 fill-current"
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold product-price-current">
            ${product.price.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
          {onAddToCart ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToCart();
              }}
              className="flex items-center text-blue-600 hover:text-blue-700"
              disabled={product.stock_level === 0}
            >
              <ShoppingCart className="w-5 h-5 mr-1" />
              <span className="font-medium">
                {product.stock_level === 0 ? 'Out of Stock' : 'Add to Cart'}
              </span>
            </button>
          ) : (
            <div className="flex items-center text-blue-600 hover:text-blue-700">
              <ShoppingCart className="w-5 h-5 mr-1" />
              <span className="font-medium">View Details</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
